/**
 * Todo API 服务
 *
 * 封装 @zero/api 包的 Todo SDK
 * 提供错误处理、类型转换和用户友好的提示
 * 集成 IDaaS 认证，自动携带认证头
 */

import {
  client,
  type TodoV1AssignTodoResponse,
  type TodoV1BatchUpdateTodosRequest,
  type TodoV1BatchUpdateTodosResponse,
  type TodoV1CreateCommentResponse,
  type TodoV1CreateTodoResponse,
  type TodoV1DeleteCommentResponse,
  type TodoV1DeleteTodoResponse,
  type TodoV1GetTodoResponse,
  type TodoV1ListActivityResponse,
  type TodoV1ListCommentsResponse,
  type TodoV1ListTodosResponse,
  type TodoV1UnassignTodoResponse,
  type TodoV1UpdateCommentResponse,
  type TodoV1UpdateTodoResponse,
  todoServiceAssignTodo,
  todoServiceBatchUpdateTodos,
  todoServiceCreateComment,
  todoServiceCreateTodo,
  todoServiceDeleteComment,
  todoServiceDeleteTodo,
  todoServiceGetTodo,
  todoServiceListActivity,
  todoServiceListComments,
  todoServiceListTodos,
  todoServiceUnassignTodo,
  todoServiceUpdateComment,
  todoServiceUpdateTodo,
} from "@zero/api";
import { Priority } from "@zero/types/enum";
import type { ActivityItem, CommentItem, TodoFilter, UpdatableTodoStatus } from "@zero/types/todo";
import { toast } from "sonner";
import { API_CONFIG } from "../config/api";
import type { KanbanColumnId } from "../types/kanban";
import {
  API_TODO_STATUS,
  type ApiTodoPriority,
  type ApiTodoState,
  type CreateTodoParams,
  fromApiActivity,
  fromApiComment,
  fromApiTodo,
  TODO_STATUS,
  type TodoItem,
  toApiCreateTodo,
  toApiUpdateTodo,
  type UpdateTodoParams,
} from "./adapters";

type GeneratedResult<T> = {
  data?: T;
  error?: unknown;
};

function errorMessage(error: unknown, fallbackMessage: string): string {
  if (typeof error !== "object" || error === null) {
    return fallbackMessage;
  }
  const maybeError = error as { error?: { message?: string }; message?: string };
  return maybeError.error?.message ?? maybeError.message ?? fallbackMessage;
}

function unwrapResponse<T>(response: GeneratedResult<T>, fallbackMessage: string): T {
  if (response.error) {
    throw new Error(errorMessage(response.error, fallbackMessage));
  }
  if (response.data === undefined) {
    throw new Error(fallbackMessage);
  }
  return response.data;
}

function requireTodo(
  response:
    | TodoV1AssignTodoResponse
    | TodoV1CreateTodoResponse
    | TodoV1GetTodoResponse
    | TodoV1UnassignTodoResponse
    | TodoV1UpdateTodoResponse,
  fallbackMessage: string,
) {
  if (!response.todo) {
    throw new Error(fallbackMessage);
  }
  return response.todo;
}

client.setConfig({ baseURL: API_CONFIG.BASE_URL });
client.instance.interceptors.request.use((config) => {
  Object.assign(config.headers, API_CONFIG.getAuthHeaders());
  return config;
});

/**
 * Todo API 服务类
 */
export class TodoApiService {
  /**
   * 获取当前用户 ID
   */
  private static get userId(): string {
    return API_CONFIG.getUserId();
  }

  /**
   * 获取 Todo 列表
   */
  static async listTodos(filter?: TodoFilter): Promise<TodoItem[]> {
    try {
      // 映射前端过滤器到 API 参数
      const status = TodoApiService.mapStatus(filter?.status);
      const priority = TodoApiService.mapPriority(filter?.priority);

      const response = await todoServiceListTodos({
        query: {
          pageSize: 100,
          state: status,
          priority,
          tags: filter?.category ? [filter.category] : undefined,
          search: filter?.search,
        },
      });

      const data = unwrapResponse<TodoV1ListTodosResponse>(response, "获取任务列表失败");
      const items = data.todos ?? [];
      return items.map((todo) => fromApiTodo(todo, TodoApiService.userId));
    } catch (error) {
      console.error("List todos error:", error);
      toast.error("获取任务列表失败");
      throw error;
    }
  }

  /**
   * 创建 Todo
   */
  static async createTodo(params: CreateTodoParams): Promise<TodoItem> {
    try {
      const apiRequest = toApiCreateTodo(params, TodoApiService.userId);
      const response = await todoServiceCreateTodo({ body: apiRequest });

      const data = unwrapResponse<TodoV1CreateTodoResponse>(response, "创建任务失败");
      const newTodo = fromApiTodo(requireTodo(data, "创建任务失败"), TodoApiService.userId);
      toast.success("任务创建成功");
      return newTodo;
    } catch (error) {
      console.error("Create todo error:", error);
      toast.error("创建任务失败");
      throw error;
    }
  }

  /**
   * 更新 Todo
   */
  static async updateTodo(id: string, params: UpdateTodoParams): Promise<TodoItem> {
    try {
      const apiRequest = toApiUpdateTodo(params);

      const response = await todoServiceUpdateTodo({ path: { id }, body: apiRequest });

      const data = unwrapResponse<TodoV1UpdateTodoResponse>(response, "更新任务失败");
      const updatedTodo = fromApiTodo(requireTodo(data, "更新任务失败"), TodoApiService.userId);
      toast.success("任务更新成功");
      return updatedTodo;
    } catch (error) {
      console.error("Update todo error:", error);
      toast.error("更新任务失败");
      throw error;
    }
  }

  /**
   * 删除 Todo
   */
  static async deleteTodo(id: string): Promise<void> {
    try {
      const response = await todoServiceDeleteTodo({ path: { id } });

      unwrapResponse<TodoV1DeleteTodoResponse>(response, "删除任务失败");
      toast.success("任务已删除");
    } catch (error) {
      console.error("Delete todo error:", error);
      toast.error("删除任务失败");
      throw error;
    }
  }

  /**
   * 标记完成/未完成
   */
  static async transitionTodo(id: string, status: UpdatableTodoStatus): Promise<TodoItem> {
    return TodoApiService.updateTodo(id, { status });
  }

  /**
   * 移动任务到指定列（看板视图）
   */
  static async moveTodoToColumn(id: string, columnId: KanbanColumnId): Promise<TodoItem> {
    try {
      switch (columnId) {
        case "todo":
          throw new Error("不支持将任务移回待处理");
        case "in-progress":
          return TodoApiService.transitionTodo(id, TODO_STATUS.inProgress);
        case "done":
          return TodoApiService.transitionTodo(id, TODO_STATUS.completed);
      }
    } catch (error) {
      console.error("Move todo to column error:", error);
      toast.error("移动任务失败");
      throw error;
    }
  }

  /**
   * 批量更新 Todos
   */
  static async batchUpdateTodos(
    updates: Array<{
      id: string;
      status: Exclude<ApiTodoState, "PENDING">;
    }>,
  ): Promise<TodoItem[]> {
    try {
      if (updates.length === 0) {
        return TodoApiService.listTodos();
      }

      const request: TodoV1BatchUpdateTodosRequest = {
        ids: updates.map((u) => u.id),
        state: updates[0].status,
      };

      const response = await todoServiceBatchUpdateTodos({ body: request });

      const data = unwrapResponse<TodoV1BatchUpdateTodosResponse>(response, "批量更新失败");
      toast.success(`成功更新 ${data.updatedCount ?? 0} 个任务`);

      // 返回更新后的 todos（需要重新获取或从响应中解析）
      // 这里简化为重新获取列表
      return TodoApiService.listTodos();
    } catch (error) {
      console.error("Batch update todos error:", error);
      toast.error("批量更新失败");
      throw error;
    }
  }

  static async deleteTodos(ids: string[]): Promise<void> {
    try {
      for (const id of ids) {
        const response = await todoServiceDeleteTodo({ path: { id } });
        unwrapResponse<TodoV1DeleteTodoResponse>(response, "批量删除失败");
      }
      toast.success(`成功删除 ${ids.length} 个任务`);
    } catch (error) {
      console.error("Batch delete todos error:", error);
      toast.error("批量删除失败");
      throw error;
    }
  }

  /**
   * 映射前端状态到后端状态
   */
  private static mapStatus(status?: TodoFilter["status"]): ApiTodoState | undefined {
    if (status === "pending") return API_TODO_STATUS.pending;
    if (status === "inProgress") return API_TODO_STATUS.inProgress;
    if (status === "completed") return API_TODO_STATUS.completed;
    if (status === "archived") return API_TODO_STATUS.archived;
    return undefined;
  }

  /**
   * 映射前端优先级到后端优先级
   */
  private static mapPriority(priority?: Priority): ApiTodoPriority | undefined {
    if (priority === Priority.Low) return "TODO_PRIORITY_LOW";
    if (priority === Priority.Medium) return "TODO_PRIORITY_MEDIUM";
    if (priority === Priority.High) return "TODO_PRIORITY_HIGH";
    if (priority === Priority.Urgent) return "TODO_PRIORITY_URGENT";
    return undefined;
  }

  static async getTodo(id: string): Promise<TodoItem> {
    try {
      const response = await todoServiceGetTodo({ path: { id } });
      const data = unwrapResponse<TodoV1GetTodoResponse>(response, "任务不存在");
      return fromApiTodo(requireTodo(data, "任务不存在"), TodoApiService.userId);
    } catch (error) {
      console.error("Get todo error:", error);
      toast.error("获取任务详情失败");
      throw error;
    }
  }

  static async assignTodo(id: string, assigneeId: string): Promise<TodoItem> {
    const nextAssigneeId = assigneeId.trim();
    if (!nextAssigneeId) {
      throw new Error("请选择指派人");
    }
    try {
      const response = await todoServiceAssignTodo({ path: { id }, body: { assigneeId: nextAssigneeId } });
      const data = unwrapResponse<TodoV1AssignTodoResponse>(response, "指派任务失败");
      const todo = fromApiTodo(requireTodo(data, "指派任务失败"), TodoApiService.userId);
      toast.success("任务已指派");
      return todo;
    } catch (error) {
      console.error("Assign todo error:", error);
      toast.error("指派任务失败");
      throw error;
    }
  }

  static async unassignTodo(id: string): Promise<TodoItem> {
    try {
      const response = await todoServiceUnassignTodo({ path: { id } });
      const data = unwrapResponse<TodoV1UnassignTodoResponse>(response, "取消指派失败");
      const todo = fromApiTodo(requireTodo(data, "取消指派失败"), TodoApiService.userId);
      toast.success("已取消指派");
      return todo;
    } catch (error) {
      console.error("Unassign todo error:", error);
      toast.error("取消指派失败");
      throw error;
    }
  }

  static async listComments(todo: TodoItem): Promise<CommentItem[]> {
    try {
      const response = await todoServiceListComments({
        path: { todoId: todo.id },
        query: { pageSize: 100 },
      });
      const data = unwrapResponse<TodoV1ListCommentsResponse>(response, "获取评论失败");
      return (data.comments ?? []).map((comment) => fromApiComment(comment, todo, TodoApiService.userId));
    } catch (error) {
      console.error("List comments error:", error);
      toast.error("获取评论失败");
      throw error;
    }
  }

  static async createComment(todo: TodoItem, content: string): Promise<CommentItem> {
    const nextContent = content.trim();
    if (!nextContent || new TextEncoder().encode(nextContent).length > 2000) {
      throw new Error("评论内容长度必须为 1-2000 字节");
    }
    try {
      const response = await todoServiceCreateComment({ path: { todoId: todo.id }, body: { content: nextContent } });
      const data = unwrapResponse<TodoV1CreateCommentResponse>(response, "创建评论失败");
      if (!data.comment) {
        throw new Error("创建评论失败");
      }
      const comment = fromApiComment(data.comment, todo, TodoApiService.userId);
      toast.success("评论已发布");
      return comment;
    } catch (error) {
      console.error("Create comment error:", error);
      toast.error("创建评论失败");
      throw error;
    }
  }

  static async updateComment(todo: TodoItem, commentId: string, content: string): Promise<CommentItem> {
    const nextContent = content.trim();
    if (!nextContent || new TextEncoder().encode(nextContent).length > 2000) {
      throw new Error("评论内容长度必须为 1-2000 字节");
    }
    try {
      const response = await todoServiceUpdateComment({
        path: { todoId: todo.id, commentId },
        body: { content: nextContent },
      });
      const data = unwrapResponse<TodoV1UpdateCommentResponse>(response, "更新评论失败");
      if (!data.comment) {
        throw new Error("更新评论失败");
      }
      const comment = fromApiComment(data.comment, todo, TodoApiService.userId);
      toast.success("评论已更新");
      return comment;
    } catch (error) {
      console.error("Update comment error:", error);
      toast.error("更新评论失败");
      throw error;
    }
  }

  static async deleteComment(todoId: string, commentId: string): Promise<void> {
    try {
      const response = await todoServiceDeleteComment({ path: { todoId, commentId } });
      unwrapResponse<TodoV1DeleteCommentResponse>(response, "删除评论失败");
      toast.success("评论已删除");
    } catch (error) {
      console.error("Delete comment error:", error);
      toast.error("删除评论失败");
      throw error;
    }
  }

  static async listActivity(todoId: string): Promise<ActivityItem[]> {
    try {
      const response = await todoServiceListActivity({
        path: { todoId },
        query: { pageSize: 100 },
      });
      const data = unwrapResponse<TodoV1ListActivityResponse>(response, "获取活动失败");
      return (data.activities ?? []).map(fromApiActivity);
    } catch (error) {
      console.error("List activity error:", error);
      toast.error("获取活动失败");
      throw error;
    }
  }
}
